import React, { Component } from 'react';

import { inject, observer } from 'mobx-react';

import {
  Button, Container, Form, Loader
} from 'semantic-ui-react';

import ReactModalDraggable from 'react-modal-resizable-draggable';
import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';
import { ASSIGNMENT_STATUS, ASSIGNMENT_TYPE } from '../../managers/AssignmentManager';
import { CONTENT_MODE, PLAYER_TYPES } from '../../managers/ContentManager';
import StudentHelperService from '../../services/StudentHelperService';
import LaunchContextService from '../../services/LaunchContextService';
import AssignmentService from '../../services/AssignmentService';
import ClassroomService from '../../services/ClassroomService';
import ImageService from '../../services/ImageService';
import NavigationService from '../../services/NavigationService';
import Auth from '../../managers/AuthManager';

import '../../css/PresentAssignmentModal.less';

export default @inject(
  'accommodationsManager', 'assignmentManager',
  'classroomManager', 'contentManager', 'courseManager', 'groupsManager',
  'reportIdentityManager', 'reportStandardsClassroomDetailStudentsManager', 'userManager')
@observer
class PresentAssignmentModal extends Component {
  constructor(props) {
    super(props);

    this.state = this.getInitialState();

    this.ModalBannerDraggable = SatCoreComponent('ModalBannerDraggable');
    this.SCRadio = SatCoreComponent('SCRadio');

    this.modalRef = React.createRef();
  }

  getInitialState() {
    const {
      assignType,
      isReassign
    } = this.props;

    const assignmentType = assignType || ASSIGNMENT_TYPE.CLASSROOM_USER;

    return {
      addingAssignment: false,
      assignmentType,
      isReassign: isReassign || false,
      selectedAssignmentId: null,
      presentationMode: 'new'
    };
  }

  async componentDidMount() {
    const { courseElement } = this.props;

    if (this.modalRef && this.modalRef.current) {
      this.modalRef.current.focus();
    }

    const status = [ASSIGNMENT_STATUS.LOCKED, ASSIGNMENT_STATUS.READY, ASSIGNMENT_STATUS.STARTED];
    await StudentHelperService.fetchPresentationAssignments({ courseResourceElementId: courseElement.elementId, status });
  }

  // eslint-disable-next-line react/sort-comp
  closePresentAssignmentModal = ({ shouldTriggerScrollRestore = true } = {}) => {
    const { closePresentAssignment, handleScrollRestorePendingIfApplicable } = this.props;

    this.setState(this.getInitialState());
    closePresentAssignment();
    if (shouldTriggerScrollRestore) {
      handleScrollRestorePendingIfApplicable?.();
    }
  };

  resolveClassroomId = () => {
    const { classroomId: classroomIdProp, classroomManager, courseElement } = this.props;
    const classroomId = classroomIdProp
      || courseElement?.classroomId
      || ClassroomService.getCurrentClassroomId({ alsoUpdateObservableWithUrlParamIfNull: true })
      || classroomManager.currentClassroomId
      || null;
    if (classroomId && classroomId !== classroomManager.currentClassroomId) {
      classroomManager.setCurrentClassroomId(classroomId);
    }
    return classroomId;
  };

  writeCreatingPresentationMessage = (targetWindow, message) => {
    if (!targetWindow) {
      return;
    }

    try {
      targetWindow.document.open();
      targetWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset='utf-8' />
    <title>${message}</title>
    <style>
      :root {
        color-scheme: light;
      }
      body {
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        font-size: 16px;
        height: 100vh;
        margin: 0;
        background: #f7f7f7;
        color: #555;
      }
      .wrapper {
        display: flex;
        align-items: center;
        gap: 16px;
      }
      .spinner {
        width: 48px;
        height: 48px;
        border: 4px solid rgba(0, 0, 0, 0.1);
        border-top-color: #4183c4;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    </style>
  </head>
  <body>
    <div class='wrapper'>
      <div class='spinner' aria-hidden='true'></div>
      <div>${message}</div>
    </div>
  </body>
</html>`);
      targetWindow.document.close();
    } catch (error) {
      console.warn('writeCreatingPresentationMessage: unable to write message markup', error);
    }
  };

  submitPresentAssignment = async (newWindow = false) => {
    let launchWindow = null;
    let shouldShowErrorInWindow = false;
    try {
      const { 
        handlePresent, 
        fromSatelliteTab, 
        prepareSatelliteLaunchWindow, 
        assignmentManager,
        contentManager,
        userManager,
        classroomManager,
        courseManager,
        courseElement,
        t
      } = this.props;
      const { addingAssignment, selectedAssignmentId, presentationMode } = this.state;

      if (addingAssignment) {
        return;
      }

      if (fromSatelliteTab) {
        prepareSatelliteLaunchWindow?.();
      }

      this.setState({ addingAssignment: true });

      let assignment;

      if (presentationMode === 'new') {
        const classroomId = this.resolveClassroomId();
        if (!classroomId) {
          this.setState({ addingAssignment: false });
          throw new Error(t('missingClassroomId', 'A classroom is required to create a presentation.'));
        }

        if (newWindow && !fromSatelliteTab) {
          // For new window, create launch context with creation parameters
          // The new window will handle creating the assignment
          const { elementId, contentItemId } = courseElement;
          const courseContentItemId = courseElement.courseContentItemId || courseManager.currentCourseId;

          const createPresentationContext = {
            createNewPresentation: true,
            contentItemId,
            courseResourceElementId: elementId,
            classroomId,
            courseContentItemId,
            meta: {
              sourceRoute: NavigationService.getCurrentRouterUrl()
            }
          };

          const launchId = LaunchContextService.storeContext(createPresentationContext);
          if (!launchId) {
            this.setState({ addingAssignment: false });
            throw new Error(t('submitPresentAssignmentError', 'Error submitting presentation'));
          }

          // Build URL
          const routerUrl = `/player-launch?playerLaunchId=${launchId}`;
          const authKey = userManager?.authKey || Auth.authKey;
          let targetUrl = `${window.location.origin}${routerUrl}`;

          if (authKey) {
            const encodedRouterUrl = encodeURIComponent(routerUrl);
            const encodedAuthKey = encodeURIComponent(authKey);
            targetUrl = `${window.location.origin}/verification?authKey=${encodedAuthKey}#routerUrl=${encodedRouterUrl}`;
          }

          // Open window to the player-launch route which will handle creation
          window.open(targetUrl, '_blank', 'noopener');

          this.setState({ addingAssignment: false });
          this.closePresentAssignmentModal({ shouldTriggerScrollRestore: false });
          return;
        } else {
          // For same window, create the assignment here
          const { elementId, contentItemId } = courseElement;
          const courseContentItemId = courseElement.courseContentItemId || courseManager.currentCourseId;

          try {
            // Create the assignment
            await assignmentManager.presentCreateAssignment(
              contentItemId,
              elementId,
              classroomId,
              courseContentItemId);

            assignment = assignmentManager.getLastAddedAssignment();
            if (!assignment) {
              throw new Error(t('failedToCreatePresentation', 'Failed to create presentation'));
            }
          } catch (error) {
            this.setState({ addingAssignment: false });
            throw error;
          }
        }
      } else if (presentationMode === 'load' && selectedAssignmentId) {
        // Load previous assignment
        assignment = assignmentManager.presentationAssignments.get(selectedAssignmentId);
      }

      if (!assignment) {
        this.setState({ addingAssignment: false });
        if (launchWindow) {
          const errorMessage = t('noAssignmentFound', 'No assignment found');
          this.writeCreatingPresentationMessage(launchWindow, errorMessage);
        }
        return;
      }

      if (newWindow && !fromSatelliteTab) {
        // Handle activity starting if needed (same as regular Open button)
        const activityId = assignment.activityId || assignment.id;
        const activityInstanceId = assignment.activityInstanceId || assignment.id;
        
        if (ASSIGNMENT_STATUS.getCheckableStatuses().includes(assignment.status)) {
          const isReady = await assignmentManager.checkActivity(activityId);
          if (isReady) {
            const isOk = await assignmentManager.startActivity(activityInstanceId, activityId, false);
            if (isOk === false) {
              this.setState({ addingAssignment: false });
              return;
            }
          } else {
            this.setState({ addingAssignment: false });
            return;
          }
        }

        // Set teacher presenting state
        contentManager.setIsTeacherPresenting(true);
        contentManager.setPresentationContext({
          activityId,
          activityInstanceId,
          assignmentId: assignment.id,
          classroomId: this.resolveClassroomId()
        });

        // Get player options - use same path as regular Open button (getOptionsForStudentAssessment)
        // This avoids calling fetchLessonPlayerUrl, matching the behavior of the Open button
        const option = await contentManager.getOptionsForStudentAssessment(
          assignment,
          window.location.origin,
          userManager.isTeacher,
          userManager.userId
        );

        if (!option || !option.playerType || !option.viewUrl) {
          this.setState({ addingAssignment: false });
          const { t } = this.props;
          throw new Error(t('submitPresentAssignmentError', 'Error submitting presentation'));
        }

        const { playerType, viewUrl, isFlowpaper } = option;
        const contentImageUrl = ImageService.getImageUrl(assignment);
        const { resourceWebTitle, resourceWebSubtitle } = AssignmentService.getAssignmentTitleAndDescription(assignment, false);

        // Create player state
        // The assignment object could be an activity instance or an activity so check the ids
        // (activityId and activityInstanceId already determined above)
        
        const playerState = {
          playerType,
          contentUrl: viewUrl,
          contentImageUrl,
          contentName: resourceWebTitle,
          contentSubtitle: resourceWebSubtitle,
          contentMode: CONTENT_MODE.TEACHER_PRESENT,
          isFlowpaper,
          assignmentId: assignment.id,
          contentItemId: assignment.contentItemId,
          previewContentType: assignment.contentItemEntityTypeId || assignment.entityTypeId,
          previewContentItemId: assignment.id,
          resourceName: assignment.name,
          instruction: assignment.instruction || assignment.activityInstruction || '',
          activityId,
          activityInstanceId,
          classroomId: this.resolveClassroomId(),
          grade: assignment.grade
        };

        // Add session ID for docreader if needed
        if (playerType === PLAYER_TYPES.DOCREADER_VIEWER) {
          playerState.sessionId = contentManager.currentflowPaperSessionId;
          playerState.docReaderUrl = viewUrl;
        }

        // Create launch context
        const launchContext = {
          playerState,
          meta: {
            sourceRoute: NavigationService.getCurrentRouterUrl()
          }
        };

        const launchId = LaunchContextService.storeContext(launchContext);
        if (!launchId) {
          this.setState({ addingAssignment: false });
          const { t } = this.props;
          throw new Error(t('submitPresentAssignmentError', 'Error submitting presentation'));
        }

        // Build URL
        const routerUrl = `/player-launch?playerLaunchId=${launchId}`;
        const authKey = userManager?.authKey || Auth.authKey;
        let targetUrl = `${window.location.origin}${routerUrl}`;

        if (authKey) {
          const encodedRouterUrl = encodeURIComponent(routerUrl);
          const encodedAuthKey = encodeURIComponent(authKey);
          targetUrl = `${window.location.origin}/verification?authKey=${encodedAuthKey}#routerUrl=${encodedRouterUrl}`;
        }

        // Navigate the already-opened window to the player
        if (launchWindow) {
          launchWindow.location.href = targetUrl;
        } else {
          window.open(targetUrl, '_blank', 'noopener');
        }

        this.setState({ addingAssignment: false });
        this.closePresentAssignmentModal({ shouldTriggerScrollRestore: false });
      } else {
        // Use existing handlePresent for same window
        handlePresent({ assignment, contentMode: CONTENT_MODE.TEACHER_PRESENT, newWindow });
        this.setState({ addingAssignment: false });
        this.closePresentAssignmentModal({ shouldTriggerScrollRestore: false });
      }
    } catch (error) {
      console.error(error);
      const { t } = this.props;
      const errorMessage = error instanceof Error ? error.message : (typeof error === 'string' ? error : t('submitPresentAssignmentError', 'Error submitting presentation'));
      
      // If we have a launch window open, show the error there
      if (shouldShowErrorInWindow && launchWindow && !launchWindow.closed) {
        try {
          this.writeCreatingPresentationMessage(launchWindow, errorMessage);
        } catch (e) {
          console.warn('Failed to write error message to window', e);
        }
      }
      
      this.setState({ addingAssignment: false });
      throw error;
    }
  };


  handlePresentationModeChange = (value) => {
    const { assignmentManager } = this.props;
    const presentationAssignments = Array.from(assignmentManager.presentationAssignments.values());
    
    if (value === 'load' && presentationAssignments.length > 0) {
      this.setState({ 
        presentationMode: value,
        selectedAssignmentId: presentationAssignments[0].id
      });
    } else {
      this.setState({ 
        presentationMode: value,
        selectedAssignmentId: null
      });
    }
  }

  renderSelectionContent = () => {
    const { t, assignmentManager } = this.props;
    const { SCRadio } = this;
    const { selectedAssignmentId, presentationMode } = this.state;

    const presentationAssignments = Array.from(assignmentManager.presentationAssignments.values());

    return (
      <div className='content-column'>
        <div className='presentation-description'>
          {t('presentationDescription', 'Presenting allows you to record answers to questions and utilize all the tools in a lesson as if you were the student. When you choose to present you can either create a new presentation or access one you already have in progress. All of your interactions are saved in the Gradebook.')}
        </div>
        <div className='presentation-options'>
          <SCRadio
            checked={presentationMode === 'new'}
            label={t('createNewPresentation', 'Create new presentation')}
            name='presentationOption'
            onChange={() => this.handlePresentationModeChange('new')}
            value='new'
          />
          <SCRadio
            checked={presentationMode === 'load'}
            disabled={presentationAssignments.length === 0}
            label={t('loadPreviousPresentation', 'Resume presentation')}
            name='presentationOption'
            onChange={() => this.handlePresentationModeChange('load')}
            value='load'
          />
          {presentationMode === 'load' && (
            <div className='presentation-assignments-list'>
              {presentationAssignments.map((assignment) => (
                <SCRadio
                  key={assignment.id}
                  checked={selectedAssignmentId === assignment.id}
                  label={`Start Date: ${new Date(assignment.timezoneStartDate).toLocaleDateString()} ${assignment.timezoneStartTime}`}
                  name='presentationAssignment'
                  onChange={() => this.setState({ selectedAssignmentId: assignment.id })}
                  value={assignment.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const {
      fromSatelliteTab,
      presentAssignmentOpen,
      assignmentManager,
      t
    } = this.props;

    const { addingAssignment, presentationMode, selectedAssignmentId } = this.state;

    const { ModalBannerDraggable } = this;

    const assignmentModalClassNames = 'PresentAssignmentModal';
    const className = 'assignmentModal';

    const submitDisabled = addingAssignment || (presentationMode === 'load' && !selectedAssignmentId);
    const handleSubmitError = (error, fallbackKey, fallbackMessage) => {
      const message = error instanceof Error && error?.message ?
        error.message : t(fallbackKey, fallbackMessage);
      console.log('************* error *************', message);
    };

    return (
      <div className={assignmentModalClassNames}>
        <ReactModalDraggable
          className={className}
          closeOnDimmerClick={false}
          closeOnEscape={true}
          disableKeystroke={true}
          isOpen={presentAssignmentOpen}
          onRequestClose={(event) => {
            const isOverlay = event?.target?.className === 'flexible-modal-mask';
            if (!isOverlay) {
              this.closePresentAssignmentModal();
            }
          }}>
          <div ref={this.modalRef} className='modalBody' tabIndex={-1}>
            <ModalBannerDraggable
              label={t('presentationMode', 'Presentation Mode')}
              onClose={this.closePresentAssignmentModal} />
            <div className='content'>
              <Form>
                <Container className='assignment-wrapper-modal'>
                  <Loader active={addingAssignment} />
                  {this.renderSelectionContent()}
                </Container>
              </Form>
            </div>
            <div className='actions'>
              <Button
                basic
                className='cancelButton'
                disabled={addingAssignment}
                onClick={this.closePresentAssignmentModal}
                primary>
                {t('cancel', 'Cancel')}
              </Button>

              {fromSatelliteTab ? (
                <Button
                  className='ui primary button saveButton'
                  disabled={submitDisabled}
                  onClick={() => {
                    this.submitPresentAssignment(false).catch((error) => {
                      handleSubmitError(error, 'openInNewTabError', 'Error opening in new tab');
                    });
                  }}
                  type='button'>
                  {t('openInNewWindow', 'Open In New Window')}
                </Button>
              ) : (
                <>
                  <Button
                    className='ui primary button saveButton'
                    disabled={submitDisabled}
                    onClick={() => {
                      this.submitPresentAssignment(false).catch((error) => {
                        handleSubmitError(error, 'submitPresentAssignmentError', 'Error submitting presentation');
                      });
                    }}
                    type='button'>
                    {t('open', 'Open')}
                  </Button>

                  <Button
                    className='ui primary button saveButton'
                    disabled={submitDisabled}
                    onClick={() => {
                      this.submitPresentAssignment(true).catch((error) => {
                        handleSubmitError(error, 'openInNewTabError', 'Error opening in new tab');
                      });
                    }}
                    type='button'>
                    {t('openInNewWindow', 'Open In New Window')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </ReactModalDraggable>
      </div>
    );
  }
}

SatCoreRegister('PresentAssignmentModal', PresentAssignmentModal);
